import { createSelector } from "reselect";
import moment from "moment";
import get from "lodash/get";
import find from "lodash/find";
import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import { urlParam } from "utils/helpers";
import {
  userOrdersSelector,
  numberFormatSelector,
  localeSelector,
  slotDetailsSelector,
  simReplacementFormSelector,
  workflowSelector,
  selfPickupStationDetailsSelector,
  simTypesSelector,
  simReplacementDocTypeSelector
} from "ducks/selectors";
import {
  constructLocalizedDeliveryAddress,
  getFormattedDeliveryDateAndTime,
  constructSelfPickupDetails,
  formatDisplayNumber
} from "utils/helpers";
import { DATE_STYLES, formatDate } from "utils/i18nFormatter";
import { E_SIM_DELIVERY_CONFIG_TYPE, SIM_TYPES } from "utils/constants";

const simReplacementOrderDomain = state =>
  get(state, "api.simReplacementOrderDetails.data", {});

const replaceSimConfig = state => get(state, "api.simConfig.data", {});
const eSimDeliveryConfig = state =>
  get(state, "api.eSimDeliveryConfig.data", {});
// ***************************************************************
// ****************** SIM Replacement selectors ******************
// ***************************************************************
export const simTypesListSelectorLabel = createSelector(
  replaceSimConfig,
  localeSelector,
  (simSettings, locale) => {
    const simTypes = get(simSettings, "inventory.result.simTypes", []);
    const items = simTypes?.map(item => {
      const itemName =
        item?.name === "physical_sim"
          ? get(locale, "label_PHYSICAL_SIM", "Physical SIM")
          : get(locale, "label_E_SIM", "eSIM");
      const itemValue =
        item?.name === "physical_sim" ? "PHYSICAL_SIM" : "E_SIM";
      return { ...item, label: itemName, value: itemValue };
    });
    return items?.filter(item => item?.enabled);
  }
);

export const simReplacementOrderSelector = createSelector(
  simReplacementOrderDomain,
  userOrdersSelector,
  (simReplacementOrder, orders) => {
    const request_id = urlParam("request_id");
    const order = orders.find(order => order.order_ref === request_id);

    return {
      ...order,
      initialShipmentDetails: get(
        simReplacementOrder,
        "initialShipmentDetails",
        {}
      ),
      items: get(simReplacementOrder, "items", [])
    };
  }
);

export const simReplacementTelcoReason = createSelector(
  simReplacementOrderSelector,
  simReplacementOrder => {
    const subStates = get(simReplacementOrder, "substates", {});
    return get(subStates, "TELCO_FULFILLMENT.reason");
  }
);

export const simReplacementOrderItemsSelector = createSelector(
  simReplacementOrderSelector,
  order => {
    return get(order, "items", []);
  }
);

export const isSimReplacementReKycLastAttemptSelector = createSelector(
  simReplacementOrderSelector,
  order => {
    return get(order, "last_kyc_attempt", false);
  }
);

export const simReplacementOrderItemsPropertiesSelector = createSelector(
  simReplacementOrderItemsSelector,
  items => {
    return items.find(item => item.name === "SIM_REPLACEMENT")?.properties;
  }
);

export const hasSimReplacementOrderItemsPropertiesSelector = createSelector(
  simReplacementOrderItemsPropertiesSelector,
  properties => {
    return (
      Boolean(get(properties, "NATIONALITY")) ||
      Boolean(get(properties, "DOCUMENT_TYPE")) ||
      Boolean(get(properties, "DOCUMENT_ID")) ||
      Boolean(get(properties, "DOCUMENT_EXPIRY_DATE"))
    );
  }
);

export const simReplacementDeliveryDetailsItemsSelector = createSelector(
  simReplacementOrderSelector,
  numberFormatSelector,
  localeSelector,
  slotDetailsSelector,
  simReplacementOrderItemsPropertiesSelector,
  selfPickupStationDetailsSelector,
  (
    order,
    numberFormat,
    locale,
    slotDetails,
    properties,
    shippingLocationDetails
  ) => {
    const contactNumber = formatDisplayNumber(
      get(order, "contact_number"),
      numberFormat,
      get(order, "country_code")
    );
    const alternateContactNumber = formatDisplayNumber(
      get(
        order,
        "temporary_contact_number",
        get(order, "order_user_detail.temporary_contact_number")
      ) ?? get(order, "order_user_detail.temporary_contact_number"),
      numberFormat,
      get(order, "country_code")
    );
    const contactEmail =
      get(order, "contact_email", get(order, "order_user_detail.email")) ??
      get(order, "order_user_detail.email");
    const initialShipmentDetails = get(
      order,
      "initial_shipment_details",
      get(order, "initialShipmentDetails")
    );
    const shippingDetails =
      get(order, "sim_type") === "PHYSICAL_SIM"
        ? get(
            locale,
            get(initialShipmentDetails, "deliveryMethod"),
            get(initialShipmentDetails, "deliveryMethod")
          )
        : get(locale, "esim_email_sent");
    const deliveryAddress = constructLocalizedDeliveryAddress(
      get(order, "order_delivery_detail"),
      locale
    );
    const deliveryLandMark = get(initialShipmentDetails, "landmark", "");
    const deliveryInstructions = get(
      initialShipmentDetails,
      "specialInstructions",
      ""
    );
    const deliveryDateAndTime = getFormattedDeliveryDateAndTime(
      get(initialShipmentDetails, "slot.date", "")
        ?.split("-")
        ?.reverse()
        ?.join("-"),
      null,
      slotDetails,
      locale
    );
    const selfPickupDetails = constructSelfPickupDetails(
      shippingLocationDetails,
      locale
    );

    return {
      simType: get(order, "sim_type"),
      orderReference: get(order, "order_ref"),
      contactNumber,
      alternateContactNumber,
      contactEmail,
      shippingDetails,
      deliveryAddress,
      deliveryDateAndTime,
      selfPickupDetails,
      deliveryLandMark,
      deliveryInstructions,
      personal_details_nationality: get(properties, "NATIONALITY"),
      personal_details_id_type: get(
        properties,
        "DOCUMENT_TYPE",
        ""
      ).toUpperCase(),
      personal_details_id: get(properties, "DOCUMENT_ID"),
      personal_document_expiry_date:
        get(properties, "DOCUMENT_EXPIRY_DATE") &&
        formatDate(
          moment(get(properties, "DOCUMENT_EXPIRY_DATE"), "YYYY-MM-DD"),
          {
            style: DATE_STYLES.SHORT
          }
        )
    };
  }
);

export const simReplacementFormInitialValuesSelector = createSelector(
  simReplacementOrderItemsPropertiesSelector,
  localeSelector,
  simTypesSelector,
  simReplacementDocTypeSelector,
  (properties, locale, simTypes, docTypes) => {
    const reKyc =
      urlParam("reKyc")?.toLocaleLowerCase() === "true" ? true : false;
    const pSimValue = find(
      simTypes,
      simType => simType.value === SIM_TYPES.PHYSICAL_SIM
    );
    let docTypeValue = {};
    if (docTypes.length === 1) docTypeValue = head(docTypes, {});

    if (!reKyc)
      return {
        ...(!isEmpty(docTypeValue) && {
          doc_type: {
            value: get(docTypeValue, "value"),
            label: get(docTypeValue, "label")
          }
        })
      };

    const docType = get(properties, "DOCUMENT_TYPE");
    const docId = get(properties, "DOCUMENT_ID");
    const docExpDate = get(properties, "DOCUMENT_EXPIRY_DATE");
    return {
      sim_type: {
        ...pSimValue
      },
      doc_type: {
        value: docType,
        label: get(locale, docType)
      },
      document_id: docId,
      document_expiry_date:
        docExpDate && moment(docExpDate, "YYYY-MM-DD").format("DD-MM-YYYY"),
      document_dobDay: {
        label: get(locale, `date_${docExpDate?.split("-")[2]}`),
        value: docExpDate?.split("-")[2]
      },
      document_dobMonth: {
        label: get(locale, `month_short_${docExpDate?.split("-")[1]}`),
        value: docExpDate?.split("-")[1]
      },
      document_dobYear: {
        label: docExpDate?.split("-")[0],
        value: docExpDate?.split("-")[0]
      }
    };
  }
);

export const simReplacementDocumentPayloadMappingSelector = createSelector(
  workflowSelector(),
  workflow => {
    const currentWorkflow = find(
      workflow,
      page => page.path === window.location.pathname
    );
    return get(currentWorkflow, "docPayloadMapping", {});
  }
);

export const simReplacementReKycParamsSelector = createSelector(
  simReplacementOrderItemsPropertiesSelector,
  simReplacementFormSelector,
  workflowSelector(),
  simReplacementOrderSelector,
  (properties, form, workflow, order) => {
    const currentWorkflow = find(
      workflow,
      page => page.path === window.location.pathname
    );
    const formMappingData = get(currentWorkflow, "formDocMapping", {});
    return {
      items: [
        {
          sku: "",
          name: "SIM_REPLACEMENT",
          type: "REQUEST",
          identifiedBy: "",
          properties: [
            {
              key: "SIM_REPLACEMENT_REASON",
              value: get(properties, "SIM_REPLACEMENT_REASON")
            },
            {
              key: "NATIONALITY",
              value: get(properties, "NATIONALITY")
            },
            {
              key: "DOCUMENT_TYPE",
              value: form?.doc_type?.value ?? ""
            },
            {
              key: "DOCUMENT_ID",
              value: form?.document_id ?? ""
            },
            {
              key: "DOCUMENT_EXPIRY_DATE",
              value: form?.document_expiry_date
                ? moment(form?.document_expiry_date).format("YYYY-MM-DD")
                : ""
            },
            {
              key: "TEMPORARY_CONTACT_NUMBER",
              value: get(
                order,
                "temporary_contact_number",
                get(order, "contact_number")
              )
            }
          ]
        },
        {
          sku: "",
          name: "DOCUMENT_FRONT_IMAGE",
          type: "DOCUMENT",
          identifiedBy: formMappingData?.DOCUMENT_FRONT_IMAGE ?? "frontImage",
          properties: []
        },
        {
          sku: "",
          name: "DOCUMENT_BACK_IMAGE",
          type: "DOCUMENT",
          identifiedBy: formMappingData?.DOCUMENT_BACK_IMAGE ?? "backImage",
          properties: []
        },
        {
          sku: "",
          name: "DOCUMENT_SELFIE_IMAGE",
          type: "DOCUMENT",
          identifiedBy: formMappingData?.DOCUMENT_SELFIE_IMAGE ?? "selfieImage",
          properties: []
        }
      ]
    };
  }
);

export const eSimDeliverySpecificationsSimSelector = createSelector(
  eSimDeliveryConfig,
  deliveryConfig => {
    return get(deliveryConfig, "deliverySpecifications", []);
  }
);

export const newESimDeliveryReplacementSelector = createSelector(
  eSimDeliverySpecificationsSimSelector,
  deliverySpecifications => {
    return (
      find(deliverySpecifications, [
        "orderType",
        E_SIM_DELIVERY_CONFIG_TYPE.new
      ])?.deliveryNeeded === true
    );
  }
);

export const replaceESimDeliveryReplacementSelector = createSelector(
  eSimDeliverySpecificationsSimSelector,
  deliverySpecifications => {
    return (
      find(deliverySpecifications, [
        "orderType",
        E_SIM_DELIVERY_CONFIG_TYPE.replacement
      ])?.deliveryNeeded === true
    );
  }
);

// ***************************************************************
// ***************** SIM Replacement makeSelect ******************
// ***************************************************************
export const makeSelectSimReplacementOrder = () => simReplacementOrderSelector;
export const makeSelectSimReplacementTelcoReason = () =>
  simReplacementTelcoReason;
export const makeSelectSimReplacementDeliveryDetailsItems = () =>
  simReplacementDeliveryDetailsItemsSelector;
export const makeSelectSimReplacementOrderItemsProperties = () =>
  simReplacementOrderItemsPropertiesSelector;
export const makeSelectSimReplacementFormInitialValues = () =>
  simReplacementFormInitialValuesSelector;
export const makeSelectsimReplacementPayloadDocumentMapping = () =>
  simReplacementDocumentPayloadMappingSelector;
export const makeSelectSimReplacementReKycParams = () =>
  simReplacementReKycParamsSelector;
export const makeSelectIsSimReplacementReKycLastAttempt = () =>
  isSimReplacementReKycLastAttemptSelector;
export const makeSelectHasSimReplacementOrderItemsProperties = () =>
  hasSimReplacementOrderItemsPropertiesSelector;
export const makeSelectSimTypesListLabel = () => simTypesListSelectorLabel;

// eSIM Delivery Configuration
export const makeESimDeliverySpecifications = () =>
  eSimDeliverySpecificationsSimSelector;
export const makeNewESimDeliveryNeeded = () =>
  newESimDeliveryReplacementSelector;
export const makeReplaceESimDeliveryNeeded = () =>
  replaceESimDeliveryReplacementSelector;
